import { z } from 'zod';

/**
 * Поля categories_i18n, которые UI может выбрать для перевода.
 *
 * `uri` — это маркер «пересобрать URL-slug из переведённого name». LLM его не
 * генерит; worker сам делает slugify(name) с разрешением конфликтов суффиксом
 * `-2`, `-3`, …. Имеет смысл только в паре с `name`; если `name` не выбран —
 * флаг игнорируется.
 */
export const translatableCategoryFieldSchema = z.enum([
  'name',
  'uri',
  'alt',
  'seo_title',
  'seo_description',
  'seo_h1',
  'seo_text',
  'ai_description',
  /** jsonb-массив строк → передаём как массив, восстанавливаем структуру. */
  'keywords',
  /** jsonb-массив { q, a } → передаём оба поля, восстанавливаем структуру. */
  'faq',
]);
export type TranslatableCategoryField = z.infer<typeof translatableCategoryFieldSchema>;

export const translateCategoriesRequestedSchema = z.object({
  source_lang: z.string().min(2).max(10),
  /**
   * Один или несколько target-языков. Handler гоняет каждый отдельной серией
   * батчей (нельзя смешивать языки в одном LLM-запросе — у каждого свой
   * skip-state и свой набор существующих uri).
   */
  target_langs: z.array(z.string().min(2).max(10)).min(1),
  /** Если пуст — переводим ВСЕ категории, у которых есть строка в source_lang. */
  category_ids: z.array(z.number().int().positive()).optional(),
  fields: z.array(translatableCategoryFieldSchema).min(1),
  /**
   * false (default): для каждого поля переводим только если оно пустое в target_lang.
   * true: переводим все выбранные поля независимо от текущего значения.
   */
  overwrite: z.boolean().default(false),
  prompt_id_override: z.number().int().positive().optional(),
  model_id_override: z.number().int().positive().optional(),
});

export const translateCategoriesPerCategoryStatusSchema = z.enum([
  'translated',
  'missing_in_response',
  'shape_invalid',
  'persist_failed',
]);

export const translateCategoriesBatchDetailSchema = z.object({
  index: z.number().int().positive(),
  target_lang: z.string(),
  category_ids: z.array(z.number().int().positive()),
  fields_per_category: z.record(z.array(translatableCategoryFieldSchema)),
  items_json: z.string(),
  raw_response: z.string().nullable(),
  tokens_in: z.number().int().nonnegative().nullable(),
  tokens_out: z.number().int().nonnegative().nullable(),
  cost_usd: z.number().nonnegative().nullable(),
  /** Optional for events emitted before cost provenance was introduced. */
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
  status: z.enum(['success', 'partial', 'failed_llm', 'failed_persist']),
  error: z.string().optional(),
  per_category: z
    .record(
      z.object({
        status: translateCategoriesPerCategoryStatusSchema,
        translated_fields: z.array(translatableCategoryFieldSchema).optional(),
        uri: z.string().optional(),
        error: z.string().optional(),
      }),
    )
    .optional(),
});

export const translateCategoriesProgressSchema = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  current_batch: z.number().int().nonnegative().optional(),
  /** Optional detail block for the batch that just completed. */
  batch: translateCategoriesBatchDetailSchema.optional(),
});

export const translateCategoriesStartedSchema = z.object({
  prompt_id: z.number().int().positive(),
  prompt_version: z.number().int().positive(),
  model_label: z.string().nullable(),
  system_template: z.string(),
  user_template: z.string(),
  source_lang: z.string(),
  target_langs: z.array(z.string()),
  fields: z.array(translatableCategoryFieldSchema),
  overwrite: z.boolean(),
  total_source_rows: z.number().int().nonnegative(),
  items_to_translate: z.number().int().nonnegative(),
  items_skipped: z.number().int().nonnegative(),
  total_batches: z.number().int().nonnegative(),
  batch_size: z.number().int().positive(),
  /** Per (target_lang, category) plan entry. */
  plan: z.array(
    z.object({
      target_lang: z.string(),
      category_id: z.number().int().positive(),
      fields: z.array(translatableCategoryFieldSchema),
      will_create_target: z.boolean(),
    }),
  ),
});

export const translateCategoriesDoneSchema = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  tokens_in: z.number().int().nonnegative(),
  tokens_out: z.number().int().nonnegative(),
  cost_usd: z.number().nonnegative(),
  /** Optional for backwards compatibility with persisted legacy events. */
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
});

export const translateCategoriesFailedSchema = z.object({
  error: z.string(),
  partial: translateCategoriesProgressSchema.optional(),
});

export type TranslateCategoriesRequestedPayload = z.infer<
  typeof translateCategoriesRequestedSchema
>;
export type TranslateCategoriesStartedPayload = z.infer<typeof translateCategoriesStartedSchema>;
export type TranslateCategoriesBatchDetail = z.infer<typeof translateCategoriesBatchDetailSchema>;
export type TranslateCategoriesProgressPayload = z.infer<typeof translateCategoriesProgressSchema>;
export type TranslateCategoriesDonePayload = z.infer<typeof translateCategoriesDoneSchema>;
export type TranslateCategoriesFailedPayload = z.infer<typeof translateCategoriesFailedSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Bulk Translate redesign (SPEC-BULK-TRANSLATE-REDESIGN §13) — additive event
// family using dot-naming. Coexists with the legacy `started/progress/done/
// failed` events above; the client will migrate to these in later PRs.
// ─────────────────────────────────────────────────────────────────────────────

/** Per-(category × target_lang) planning row emitted in `run.started`. */
export const translateCategoriesPlanItemSchema = z.object({
  category_id: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  fields: z.array(translatableCategoryFieldSchema),
  action: z.enum(['new', 'replace', 'skip']),
  skip_reason: z.string().optional(),
});
export type TranslateCategoriesPlanItem = z.infer<typeof translateCategoriesPlanItemSchema>;

/** Per-category row attached to a batch's `done` / `failed` event. */
export const translateCategoriesBatchRowSchema = z.object({
  category_id: z.number().int().positive(),
  uri: z.string(),
  status: z.enum(['new', 'replaced', 'skip', 'fail']),
  fields: z.array(translatableCategoryFieldSchema),
  error: z.string().optional(),
});
export type TranslateCategoriesBatchRow = z.infer<typeof translateCategoriesBatchRowSchema>;

/**
 * Emitted once after plan resolution, before the first batch fires.
 * Carries rendered prompts (already variable-substituted), full plan, and
 * the resolved model label for display.
 */
export const translateCategoriesRunStartedSchema = z.object({
  prompt_system: z.string(),
  prompt_user: z.string(),
  plan: z.array(translateCategoriesPlanItemSchema),
  model: z.string(),
});
export type TranslateCategoriesRunStartedPayload = z.infer<
  typeof translateCategoriesRunStartedSchema
>;

/** Emitted immediately before each LLM call. */
export const translateCategoriesBatchStartedSchema = z.object({
  batch_n: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  category_ids: z.array(z.number().int().positive()),
  /**
   * Stable preview keys (typically `${category_id}:${target_lang}`) the
   * client can use to mark in-flight rows in its grid.
   */
  preview_ids: z.array(z.string()),
});
export type TranslateCategoriesBatchStartedPayload = z.infer<
  typeof translateCategoriesBatchStartedSchema
>;

/** Emitted on successful batch (may include per-row `fail` statuses). */
export const translateCategoriesBatchDoneSchema = z.object({
  batch_n: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  ok_count: z.number().int().nonnegative(),
  fail_count: z.number().int().nonnegative(),
  skip_count: z.number().int().nonnegative(),
  tok_in: z.number().int().nonnegative(),
  tok_out: z.number().int().nonnegative(),
  cost: z.number().nonnegative(),
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
  rows: z.array(translateCategoriesBatchRowSchema),
});
export type TranslateCategoriesBatchDonePayload = z.infer<
  typeof translateCategoriesBatchDoneSchema
>;

/** Emitted when the LLM call itself failed (whole batch lost). */
export const translateCategoriesBatchFailedSchema = z.object({
  batch_n: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  ok_count: z.number().int().nonnegative(),
  fail_count: z.number().int().nonnegative(),
  error_code: z.string(),
  error_message: z.string(),
  tok_in: z.number().int().nonnegative(),
  tok_out: z.number().int().nonnegative(),
  cost: z.number().nonnegative(),
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
  rows: z.array(translateCategoriesBatchRowSchema),
});
export type TranslateCategoriesBatchFailedPayload = z.infer<
  typeof translateCategoriesBatchFailedSchema
>;
